import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { CheerComposeSheet } from "@/components/crew/cheer-compose-sheet"
import { Button } from "@/components/ui/button"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import { creatorFeedCheerPath } from "@/routes"
import { CHEER_REACTION_META, type CheerReaction } from "@/types/cheers"

/**
 * OKS-509 / OKS-555 — 🔥 peer cheer control on a feed post (UX Spec §1-4).
 *
 * OKS-555 replaces the OKS-509 instant one-tap with a two-mode control:
 *   - **Not cheered → tap opens `CheerComposeSheet`** (pick reaction + optional
 *     ≤140 message → Send). The feed reaction fills OPTIMISTICALLY at submit
 *     start (with the OKS-509 220ms scale pulse); on send error it rolls back.
 *   - **Cheered → tap UN-CHEERS directly** (DELETE, idempotent server-side),
 *     optimistic-empty, no sheet (UX Spec §1 un-cheer). To change reaction the
 *     creator un-cheers then re-cheers (PRD Q6 — no in-place edit in v1).
 *
 * A plain Inertia visit for the un-cheer / an Inertia `<Form>` inside the sheet
 * for the send — NOT a shadcn Form / RHF / zod (Crew rule). Hidden entirely on
 * the viewer's own post (`canCheer === false`).
 *
 * Locked testids: `feed-cheer-button`, `feed-cheer-count` (preserved from
 * OKS-509; click semantics changed — now opens the sheet instead of firing).
 */
interface FeedCheerButtonProps {
  submissionId: number
  cheered: boolean
  cheerCount: number
  canCheer: boolean
  /** The viewer's current reaction (null when not cheered). */
  viewerReaction: CheerReaction | null
  /** Recipient (post author) identity for the compose sheet mini-card. */
  recipient: {
    name: string
    handle: string | null
    avatarUrl: string | null
    rankName: string | null
  }
  postThumbnailUrl: string | null
  postCaption: string
}

export function FeedCheerButton({
  submissionId,
  cheered,
  cheerCount,
  canCheer,
  viewerReaction,
  recipient,
  postThumbnailUrl,
  postCaption,
}: FeedCheerButtonProps) {
  const { t } = useTranslation()
  const [on, setOn] = useState(cheered)
  const [reaction, setReaction] = useState<CheerReaction | null>(viewerReaction)
  const [count, setCount] = useState(cheerCount)
  const [pending, setPending] = useState(false)
  const [pulse, setPulse] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)

  if (!canCheer) return null

  const emoji = CHEER_REACTION_META[reaction ?? "fire"].emoji

  function pop() {
    setPulse(true)
    window.setTimeout(() => setPulse(false), 220)
  }

  function handleClick() {
    if (pending) return
    if (!on) {
      // Not cheered → open the compose sheet (the send happens there).
      setSheetOpen(true)
      return
    }

    // Cheered → un-cheer directly (DELETE), optimistic-empty.
    const prevCount = count
    const prevReaction = reaction
    setOn(false)
    setReaction(null)
    setCount((c) => Math.max(0, c - 1))
    setPending(true)

    router.delete(creatorFeedCheerPath(submissionId), {
      preserveScroll: true,
      preserveState: true,
      onError: () => {
        setOn(true)
        setReaction(prevReaction)
        setCount(prevCount)
        toast.error(t("crew_feed.cheer.toast_error"))
      },
      onFinish: () => setPending(false),
    })
  }

  // Optimistic fill at compose-submit start (UX Spec §3): fill before the
  // server responds. A fresh cheer bumps the count.
  function handleOptimisticSend(next: CheerReaction) {
    setCount((c) => (on ? c : c + 1))
    setOn(true)
    setReaction(next)
    setPending(true)
    pop()
  }

  function handleSendError() {
    setOn(cheered)
    setReaction(viewerReaction)
    setCount(cheerCount)
    setPending(false)
    toast.error(t("crew_feed.cheer.toast_error"))
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={pending}
        aria-pressed={on}
        aria-label={t(on ? "crew_feed.cheer.cheered" : "crew_feed.cheer.cheer")}
        data-testid="feed-cheer-button"
        data-cheered={on}
        data-reaction={on ? (reaction ?? "fire") : undefined}
        className={cn(
          "gap-1.5 rounded-full transition-transform",
          on && "border-orange-300 text-orange-600 dark:text-orange-400",
          pulse && "scale-110",
        )}
      >
        <span aria-hidden="true" className="text-base leading-none">
          {emoji}
        </span>
        <span data-testid="feed-cheer-count" className="tabular-nums">
          {formatCompactNumber(count)}
        </span>
      </Button>

      <CheerComposeSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        submissionId={submissionId}
        recipient={recipient}
        postThumbnailUrl={postThumbnailUrl}
        postCaption={postCaption}
        initialReaction={reaction ?? "fire"}
        onOptimisticSend={handleOptimisticSend}
        onSendError={handleSendError}
      />
    </>
  )
}
